import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { ProtectedRoute } from './ProtectedRoute'
import { PageLoading } from '@/components/ui/loading'
import NotFoundPage from '@/pages/errors/NotFoundPage'
import RouteErrorPage from '@/pages/errors/RouteErrorPage'

// 懒加载布局组件
const MainLayout = lazy(() => import('@/layouts/MainLayout'))

// 懒加载页面组件
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const ActivitiesPage = lazy(() => import('@/pages/activities/ActivitiesPage'))
const CreateActivityPage = lazy(() => import('@/pages/activities/CreateActivityPage'))
const ActivityDetailPage = lazy(() => import('@/pages/activities/ActivityDetailPage'))
const GenerateMatchesPage = lazy(() => import('@/pages/activities/GenerateMatchesPage'))
const ActivitySettingsPage = lazy(() => import('@/pages/activities/ActivitySettingsPage'))
const QuickAddMatchPage = lazy(() => import('@/pages/activities/QuickAddMatchPage'))
const ManualSchedulePage = lazy(() => import('@/pages/activities/ManualSchedulePage'))
const TeamBattleLineupsPage = lazy(() => import('@/pages/activities/TeamBattleLineupsPage'))
const ActivityViewsPage = lazy(() => import('@/pages/activities/ActivityViewsPage'))
const ActivityFeesPage = lazy(() => import('@/pages/activities/ActivityFeesPage'))
const ActivityFeeDetailPage = lazy(() => import('@/pages/activities/ActivityFeeDetailPage'))
const ClubsPage = lazy(() => import('@/pages/clubs/ClubsPage'))
const ClubDetailPage = lazy(() => import('@/pages/clubs/ClubDetailPage'))
const CreateClubPage = lazy(() => import('@/pages/clubs/CreateClubPage'))
const ClubMembersPage = lazy(() => import('@/pages/clubs/ClubMembersPage'))
const ClubRankingsPage = lazy(() => import('@/pages/clubs/ClubRankingsPage'))
const NearbyClubsPage = lazy(() => import('@/pages/clubs/NearbyClubsPage'))
const VenuesPage = lazy(() => import('@/pages/venues/VenuesPage'))
const VenueDetailPage = lazy(() => import('@/pages/venues/VenueDetailPage'))
const CreateVenuePage = lazy(() => import('@/pages/venues/CreateVenuePage'))
const NearbyVenuesPage = lazy(() => import('@/pages/venues/NearbyVenuesPage'))
const VenueMembersPage = lazy(() => import('@/pages/venues/VenueMembersPage'))
const VenueMemberDetailPage = lazy(() => import('@/pages/venues/VenueMemberDetailPage'))
const VenueCardTypesPage = lazy(() => import('@/pages/venues/VenueCardTypesPage'))
const VenueStatsPage = lazy(() => import('@/pages/venues/VenueStatsPage'))
const VenueMyRecordsPage = lazy(() => import('@/pages/venues/VenueMyRecordsPage'))
const MatchDetailPage = lazy(() => import('@/pages/matches/MatchDetailPage'))
const UserDetailPage = lazy(() => import('@/pages/users/UserDetailPage'))
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'))
const HistoryPage = lazy(() => import('@/pages/profile/HistoryPage'))
const EditProfilePage = lazy(() => import('@/pages/profile/EditProfilePage'))
const RacketsPage = lazy(() => import('@/pages/rackets/RacketsPage'))
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'))
const FeeCalculatorPage = lazy(() => import('@/pages/tools/FeeCalculatorPage'))
const TeamDrawPage = lazy(() => import('@/pages/tools/TeamDrawPage'))
const PreRegistrationsPage = lazy(() => import('@/pages/preRegistrations/PreRegistrationsPage'))
const PreRegistrationFormPage = lazy(() => import('@/pages/preRegistrations/PreRegistrationFormPage'))
const PreRegistrationDetailPage = lazy(() => import('@/pages/preRegistrations/PreRegistrationDetailPage'))
const DragonImportPage = lazy(() => import('@/pages/tools/DragonImportPage'))
const PreRegistrationViewsPage = lazy(() => import('@/pages/preRegistrations/PreRegistrationViewsPage'))
const HelpPage = lazy(() => import('@/pages/help/HelpPage'))

export const APP_ROUTES: RouteObject[] = [
  {
    path: '/login',
    errorElement: <RouteErrorPage />,
    element: (
      <Suspense fallback={<PageLoading />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: '',
        element: (
          <Suspense fallback={<PageLoading />}>
            <MainLayout />
          </Suspense>
        ),
        children: [
          { index: true, element: <Navigate to="activities" replace /> },
          {
            path: 'activities',
            element: (
              <Suspense fallback={<PageLoading />}>
                <ActivitiesPage />
              </Suspense>
            ),
          },
          {
            path: 'activities/create',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CreateActivityPage />
              </Suspense>
            ),
          },
          {
            path: 'activities/:id',
            element: (
              <Suspense fallback={<PageLoading />}>
                <ActivityDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'activities/:id/generate',
            element: (
              <Suspense fallback={<PageLoading />}>
                <GenerateMatchesPage />
              </Suspense>
            ),
          },
          {
            path: 'activities/:id/settings',
            element: <Suspense fallback={<PageLoading />}><ActivitySettingsPage /></Suspense>,
          },
          {
            path: 'activities/:id/quick-add',
            element: <Suspense fallback={<PageLoading />}><QuickAddMatchPage /></Suspense>,
          },
          {
            path: 'activities/:id/manual-schedule',
            element: <Suspense fallback={<PageLoading />}><ManualSchedulePage /></Suspense>,
          },
          {
            path: 'activities/:id/team-lineups',
            element: <Suspense fallback={<PageLoading />}><TeamBattleLineupsPage /></Suspense>,
          },
          {
            path: 'activities/:id/views',
            element: <Suspense fallback={<PageLoading />}><ActivityViewsPage /></Suspense>,
          },
          {
            path: 'activities/:id/fees',
            element: <Suspense fallback={<PageLoading />}><ActivityFeesPage /></Suspense>,
          },
          {
            path: 'activities/:id/fees/:calculationId',
            element: <Suspense fallback={<PageLoading />}><ActivityFeeDetailPage /></Suspense>,
          },
          {
            path: 'activities/:id/team-draw',
            element: <Suspense fallback={<PageLoading />}><TeamDrawPage /></Suspense>,
          },
          {
            path: 'draw-sessions/:sessionId',
            element: <Suspense fallback={<PageLoading />}><TeamDrawPage /></Suspense>,
          },
          {
            path: 'pre-registrations',
            element: <Suspense fallback={<PageLoading />}><PreRegistrationsPage /></Suspense>,
          },
          {
            path: 'pre-registrations/create',
            element: <Suspense fallback={<PageLoading />}><PreRegistrationFormPage /></Suspense>,
          },
          {
            path: 'pre-registrations/:id',
            element: <Suspense fallback={<PageLoading />}><PreRegistrationDetailPage /></Suspense>,
          },
          {
            path: 'pre-registrations/:id/edit',
            element: <Suspense fallback={<PageLoading />}><PreRegistrationFormPage /></Suspense>,
          },
          {
            path: 'pre-registrations/:id/views',
            element: <Suspense fallback={<PageLoading />}><PreRegistrationViewsPage /></Suspense>,
          },
          {
            path: 'clubs',
            element: (
              <Suspense fallback={<PageLoading />}>
                <ClubsPage />
              </Suspense>
            ),
          },
          {
            path: 'clubs/:id',
            element: (
              <Suspense fallback={<PageLoading />}>
                <ClubDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'clubs/create',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CreateClubPage />
              </Suspense>
            ),
          },
          {
            path: 'clubs/:id/edit',
            element: <Suspense fallback={<PageLoading />}><CreateClubPage /></Suspense>,
          },
          {
            path: 'clubs/:id/members',
            element: <Suspense fallback={<PageLoading />}><ClubMembersPage /></Suspense>,
          },
          {
            path: 'clubs/:id/rankings',
            element: <Suspense fallback={<PageLoading />}><ClubRankingsPage /></Suspense>,
          },
          {
            path: 'clubs/nearby',
            element: (
              <Suspense fallback={<PageLoading />}>
                <NearbyClubsPage />
              </Suspense>
            ),
          },
          {
            path: 'venues',
            element: (
              <Suspense fallback={<PageLoading />}>
                <VenuesPage />
              </Suspense>
            ),
          },
          {
            path: 'venues/:id',
            element: (
              <Suspense fallback={<PageLoading />}>
                <VenueDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'venues/:id/edit',
            element: <Suspense fallback={<PageLoading />}><CreateVenuePage /></Suspense>,
          },
          { path: 'venues/:id/members', element: <Suspense fallback={<PageLoading />}><VenueMembersPage /></Suspense> },
          { path: 'venues/:id/members/:userId', element: <Suspense fallback={<PageLoading />}><VenueMemberDetailPage /></Suspense> },
          { path: 'venues/:id/card-types', element: <Suspense fallback={<PageLoading />}><VenueCardTypesPage /></Suspense> },
          { path: 'venues/:id/stats', element: <Suspense fallback={<PageLoading />}><VenueStatsPage /></Suspense> },
          { path: 'venues/:id/my-records', element: <Suspense fallback={<PageLoading />}><VenueMyRecordsPage /></Suspense> },
          {
            path: 'venues/create',
            element: (
              <Suspense fallback={<PageLoading />}>
                <CreateVenuePage />
              </Suspense>
            ),
          },
          {
            path: 'venues/nearby',
            element: (
              <Suspense fallback={<PageLoading />}>
                <NearbyVenuesPage />
              </Suspense>
            ),
          },
          {
            path: 'matches/:id',
            element: (
              <Suspense fallback={<PageLoading />}>
                <MatchDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'users/:id',
            element: (
              <Suspense fallback={<PageLoading />}>
                <UserDetailPage />
              </Suspense>
            ),
          },
          {
            path: 'profile',
            element: (
              <Suspense fallback={<PageLoading />}>
                <ProfilePage />
              </Suspense>
            ),
          },
          { path: 'profile/history', element: <Suspense fallback={<PageLoading />}><HistoryPage /></Suspense> },
          {
            path: 'profile/edit',
            element: (
              <Suspense fallback={<PageLoading />}>
                <EditProfilePage />
              </Suspense>
            ),
          },
          {
            path: 'rackets',
            element: (
              <Suspense fallback={<PageLoading />}>
                <RacketsPage />
              </Suspense>
            ),
          },
          {
            path: 'notifications',
            element: (
              <Suspense fallback={<PageLoading />}>
                <NotificationsPage />
              </Suspense>
            ),
          },
          {
            path: 'tools/fee-calculator',
            element: (
              <Suspense fallback={<PageLoading />}>
                <FeeCalculatorPage />
              </Suspense>
            ),
          },
          {
            path: 'tools/dragon-import',
            element: <Suspense fallback={<PageLoading />}><DragonImportPage /></Suspense>,
          },
          {
            path: 'help',
            element: (
              <Suspense fallback={<PageLoading />}>
                <HelpPage />
              </Suspense>
            ),
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]

export const router = createBrowserRouter(APP_ROUTES)
